import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { platformMeta, platformSolidFill } from '../../promote/platformMeta';
import { formatSignedCount } from '../homeDeltas';
import { socialBlockMessages as messages } from './socialBlockMessages';
import { followersLabel, socialRowState } from './socialSummary';
import { SocialSummaryChannel } from './socialSummaryTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Social block; each part uses its own slice of it */
import styles from './SocialBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialChannelRowProps {
  channel: SocialSummaryChannel;
  /** Opens Promote → Channels. The connect flow and the numbers of a channel are there. */
  onOpen: () => void;
}

/**
 * One channel of the Social block (design d1, d2): mark, name, handle, then the followers and
 * their change. A channel that needs a new sign-in shows "Reconnect", and a channel in its first
 * read shows "Reading your numbers". A follower count that the platform does not give is a dash
 * with no change pill, never 0. The whole row opens Channels.
 */
const SocialChannelRowComponent: React.FC<SocialChannelRowProps> = ({ channel, onOpen }) => {
  const intl = useIntl();
  const meta = platformMeta[channel.platform];
  const state = socialRowState(channel);

  return (
    <button type="button" className={styles.row} onClick={onOpen}>
      <span className={styles.mark} style={{ background: platformSolidFill(channel.platform) }}>
        <FontAwesomeIcon icon={meta.icon} />
      </span>
      <span className={styles.rowName}>
        <b>{meta.name}</b>
        {channel.handle !== null && <small>{channel.handle}</small>}
      </span>
      {state === 'reconnect' && <span className={styles.rowAction}>{intl.formatMessage(messages.reconnect)}</span>}
      {state === 'reading' && <span className={styles.rowWait}>{intl.formatMessage(messages.reading)}</span>}
      {state === 'numbers' && channel.followers === null && (
        <span className={styles.rowValue} title={intl.formatMessage(followersLabel(channel))}>
          —
        </span>
      )}
      {state === 'numbers' && channel.followers !== null && (
        <>
          <span className={styles.rowValue} title={intl.formatMessage(followersLabel(channel))}>
            {intl.formatNumber(channel.followers)}
          </span>
          {channel.followersChange === null ? (
            <DeltaPill value={0} label={intl.formatMessage(messages.firstWeek)} className={styles.rowDelta} />
          ) : (
            <DeltaPill
              value={channel.followersChange}
              label={formatSignedCount(intl, channel.followersChange)}
              className={styles.rowDelta}
            />
          )}
        </>
      )}
    </button>
  );
};

export const SocialChannelRow = TypedMemo(SocialChannelRowComponent);
